"use client";

import {
  ArrowRight,
  Zap,
  ShieldCheck,
  Database,
  LineChart,
} from "lucide-react";
import React from "react";

export default function ResearchJourney() {
  return (
    <main
      id="research-journey"
      className="relative min-h-screen overflow-hidden bg-[#0e2a3f] text-white"
    >
      <div className="pointer-events-none absolute inset-0 [background:radial-gradient(1000px_600px_at_70%_-10%,rgba(255,255,255,0.12),transparent_50%),radial-gradient(800px_500px_at_10%_10%,rgba(73,166,233,0.15),transparent_50%),radial-gradient(600px_400px_at_50%_100%,rgba(224,168,93,0.12),transparent_55%)]" />

      <section className="relative mx-auto max-w-6xl px-6 pb-24 pt-14">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs font-semibold backdrop-blur-md">
            <ShieldCheck size={14} /> Proven Research Impact
          </span>
          <h1 className="mt-5 text-4xl font-semibold tracking-tight">
            The Research Journey Transformed
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-white/80">
            Accelerate Discovery. Streamline months of data preparation into
            weeks of productive research—spend your time on insights, not
            infrastructure.
          </p>
        </div>

        {/* Duration pill */}
        <div className="mx-auto mt-10 flex max-w-lg items-center justify-between rounded-2xl border border-white/15 bg-white/5 px-6 py-4 text-sm shadow-inner backdrop-blur-md">
          <div className="grid place-items-center rounded-xl border border-white/10 px-5 py-3 ring-1 ring-amber-300">
            <div className="text-md text-[#A6DDDD]">Status Quo</div>
            <div className="text-xl font-semibold">10-60 Weeks</div>
          </div>
          <ArrowRight className="mx-4 shrink-0 text-amber-300" />
          <div className="grid place-items-center rounded-xl border border-white/10 px-5 py-3 ring-1 ring-amber-300">
            <div className="text-md text-[#A6DDDD]">Accelerator</div>
            <div className="text-xl font-semibold">1-12 Weeks</div>
          </div>
        </div>

        {/* FLOW DIAGRAM */}
        <div className="relative mt-24">
          {/* SVG dashed connectors */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 hidden lg:block"
          >
            {/* Single tall S-curve that snakes through the three rows */}
            <img
              src="/images/Vector 1.png"
              alt=""
              className="
        /*
        nudge  horizontally until the dashes pass through card pins */ /* nudge
        vertically to line up with row 1 */ /* overall height
        of
        the   path; tweak to match your rows */ absolute left-[52%] top-[-10px] h-[980px] w-auto
        -translate-x-1/2
        select-none
        opacity-90
      "
              draggable={false}
            />
          </div>

          {/* Grid canvas with zig‑zag placement and more spacing */}
          <div className="relative grid grid-cols-1 gap-y-16 lg:grid-cols-12 lg:gap-x-8 lg:gap-y-24">
            {/* Row 1 (left, smaller card) */}
            <FlowCard
              pin="right"
              size="sm"
              icon={<IdeaIcon />}
              title="Discovery & Ideation"
              copy="Transform weeks of limited platform exploration into days of comprehensive multi-platform analysis."
              current="2–4 Weeks"
              accel="1–3 Days"
              pill="Up to 98% Faster"
              className="lg:col-span-4 lg:col-start-2 lg:row-start-1 lg:mt-8"
            />

            {/* Row 1 counterpart (right) */}
            <FlowCard
              pin="left"
              size="sm"
              icon={<Database className="h-4 w-4" />}
              title="Data Access"
              copy="Skip API applications and scraper development with immediate access to validated datasets."
              current="1–6 Months"
              accel="Instant"
              pill="Up to 98% Faster"
              className="lg:col-span-4 lg:col-start-8 lg:row-start-1 lg:mt-24"
            />

            {/* Row 2 (left) */}
            <FlowCard
              pin="right"
              size="sm"
              icon={<Database className="h-4 w-4" />}
              title="Data Preparation & Cleaning"
              copy="Research-ready data with pre-built routines eliminates months of cleaning and preprocessing."
              current="1-6 Months"
              accel="1-3 Days"
              pill="Up to 98% Faster"
              className="lg:col-span-4 lg:col-start-2 lg:row-start-2 lg:mt-20"
            />

            {/* Row 2 counterpart (right) */}
            <FlowCard
              pin="left"
              size="sm"
              icon={<LineChart className="h-4 w-4" />}
              title="Analysis"
              copy="Built-in tools and cloud infrastructure accelerate analysis without setup overhead."
              current="3 Days – 2 months"
              accel="Instant"
              pill="Remove Setup Time"
              className="lg:col-span-4 lg:col-start-8 lg:row-start-2 lg:mt-8"
            />

            {/* Row 3 (left) */}
            <FlowCard
              pin="right"
              size="sm"
              icon={<ShieldCheck className="h-4 w-4" />}
              title="Ethically Review & Export"
              copy="Export aggregate data tables and charts, protecting personally identifiable information (PII)."
              className="lg:col-span-4 lg:col-start-3 lg:row-start-3 lg:mt-20"
            />

            {/* Row 3 counterpart (right) */}
            <FlowCard
              pin="left"
              size="sm"
              icon={<Zap className="h-4 w-4" />}
              title="Retain, Revisit, Reproduce"
              copy="Retain shared data and projects; reproduce studies or onboard collaborators quickly."
              className="lg:col-span-4 lg:col-start-8 lg:row-start-3 lg:mt-4"
            />
          </div>
        </div>
      </section>
    </main>
  );
}

function FlowCard({
  icon,
  title,
  copy,
  current,
  accel,
  pill,
  className = "",
  pin = "right",
  size = "md",
}: {
  icon: React.ReactNode;
  title: string;
  copy: string;
  current?: string;
  accel?: string;
  pill?: string;
  className?: string;
  pin?: "left" | "right";
  size?: "sm" | "md";
}) {
  const pad = size === "sm" ? "p-4" : "p-5";
  const text = size === "sm" ? "text-[15px]" : "text-base";
  const badgeText = size === "sm" ? "text-[11px]" : "text-xs";
  const width = size === "sm" ? "max-w-sm" : "max-w-md";

  return (
    <div
      className={
        `relative ${width} rounded-2xl border border-white/15 bg-white/5 ${pad} shadow-xl shadow-black/20 backdrop-blur-md ` +
        className
      }
    >
      {/* golden pin aligned to card mid-side to meet the path */}
      <div
        className={`absolute top-1/2 -translate-y-1/2 ${pin === "left" ? "-left-2" : "-right-2"} h-3 w-3 rounded-full bg-amber-300 shadow-[0_0_0_4px] shadow-amber-300/30`}
      />
      <div className="mt-2 flex items-start gap-3">
        <span className="grid h-7 w-14 place-content-center rounded-full bg-white/10 text-white/90">
          {icon}
        </span>
        <div className="">
          <h3
            className={`font-semibold tracking-tight ${size === "sm" ? "text-base" : "text-lg"}`}
          >
            {title}
          </h3>
          <p className={`mt-1 max-w-prose text-white/80 ${text}`}>{copy}</p>

          {(current || accel) && (
            <div className={`mt-4 flex flex-col gap-2 ${badgeText}`}>
              {current && (
                <div className="inline-flex items-center gap-2">
                  <Badge label="Current Standard" variant="outline" />
                  <span className="rounded-md bg-white/10 px-2 py-1 font-medium text-white/80">
                    {current}
                  </span>
                </div>
              )}
              {accel && (
                <div className="inline-flex items-center gap-2">
                  <Badge label="Accelerator" />
                  <span className="rounded-md bg-white px-2 py-1 font-semibold text-[#0e2a3f]">
                    {accel}
                  </span>
                </div>
              )}
            </div>
          )}

          {pill && (
            <div className="mt-4 inline-flex rounded-full bg-emerald-300/15 px-3 py-1 text-[11px] font-semibold text-emerald-200 ring-1 ring-inset ring-emerald-300/25">
              {pill}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Badge({
  label,
  variant = "solid",
}: {
  label: string;
  variant?: "solid" | "outline";
}) {
  return (
    <span
      className={
        variant === "solid"
          ? "inline-flex items-center gap-1 rounded-md bg-sky-400/20 px-2 py-1 font-semibold text-sky-200 ring-1 ring-inset ring-sky-300/30"
          : "inline-flex items-center gap-1 rounded-md border border-white/20 bg-white/5 px-2 py-1 font-semibold text-white/80"
      }
    >
      {label}
    </span>
  );
}

function IdeaIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      className="h-4 w-4"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M9 18h6" />
      <path d="M9 21h6" />
      <path d="M12 2a7 7 0 00-4 12c.6.5 1 1.4 1 2.3V17h6v-.7c0-.9.4-1.8 1-2.3A7 7 0 0012 2z" />
    </svg>
  );
}
